/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useId, useState } from 'react'
import Highcharts from 'highcharts'
import HC_data from 'highcharts/modules/data'
import { LineChart as LineChartType } from '../../types/StatTypes'
import ChartSkeleton from '../skeleton/ChartSkeleton'
import BaseChart from './BaseChart'
interface IGradientLineChartProps {
  chartData: LineChartType
  textColor: string
  rangeDescription: string
  height?: number
  showLegend?: boolean
  tickInterval?: number
  gradientStops: [number, number[]][]
  gridColor?: string
  max?: number
}

const GradientLineChart = (props: IGradientLineChartProps) => {
  const {
    chartData,
    textColor,
    rangeDescription,
    gradientStops,
    height,
    showLegend = true,
    tickInterval = 24 * 3600 * 1000,
    max = 100,
    gridColor = '#E0E0E0',
  } = props
  const [isLoading, setIsLoading] = useState(true)
  const chartId = useId()

  function getInterpolatedColor(value: number) {
    const normalizedValue = Math.min(Math.max(value, 0), 100) / 100
    const colorIndex = gradientStops.findIndex(([stop]) => stop >= normalizedValue) - 1
    const lowerStop = gradientStops[Math.max(colorIndex, 0)]
    const upperStop = gradientStops[Math.min(colorIndex + 1, gradientStops.length - 1)]
    const stopDiff = upperStop[0] - lowerStop[0]
    const ratio = stopDiff === 0 ? 0 : (normalizedValue - lowerStop[0]) / stopDiff
    const r = Math.round(lowerStop[1][0] + ratio * (upperStop[1][0] - lowerStop[1][0]))
    const g = Math.round(lowerStop[1][1] + ratio * (upperStop[1][1] - lowerStop[1][1]))
    const b = Math.round(lowerStop[1][2] + ratio * (upperStop[1][2] - lowerStop[1][2]))
    return `rgb(${r}, ${g}, ${b})`
  }

  useEffect(() => {
    if (chartData) {
      setIsLoading(true)
      const timer = setTimeout(() => setIsLoading(false), 400)
      return () => clearTimeout(timer)
    }
  }, [chartData])

  useEffect(() => {
    if (typeof HC_data === 'function') {
      HC_data(Highcharts)
    }
  }, [])

  const options: any = {
    chart: {
      type: 'line',
      height: height || 300,
      style: {
        fontFamily: 'inherit',
        color: textColor,
      },
      backgroundColor: 'transparent',
    },
    title: {
      text: null,
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%b %e',
      },
      accessibility: {
        rangeDescription: rangeDescription,
      },
      tickInterval: tickInterval,
      labels: {
        style: {
          color: textColor,
        },
      },
      gridLineColor: gridColor,
    },
    yAxis: {
      title: '',
      labels: {
        style: {
          color: textColor,
        },
      },
      max: max,
      lineColor: textColor,
      gridLineColor: gridColor,
    },
    legend: showLegend
      ? {
          layout: 'horizontal',
          align: 'left',
          verticalAlign: 'top',
          itemStyle: {
            color: textColor,
          },
        }
      : {
          enabled: false,
        },
    tooltip: {
      shared: true,
      crosshairs: true,
      pointFormatter: function (this: Highcharts.Point): string {
        const value = (this as any).y
        const color = getInterpolatedColor(value)
        return `<span style="color:${color}">\u25CF</span> ${this.series.name}: <b>${this.y}</b><br/>`
      },
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        colorByPoint: false, // you can keep this off if you're using `color` in points

        className: 'popup-on-click',
        marker: {
          enabled: true,
          lineWidth: 1,
          lineColor: null,
        },
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 1, y2: 0 },
          stops: gradientStops.map(([position, rgb]) => {
            return [position, `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`]
          }),
        },
      },
    },
    series: chartData.series.map((s: { data: [string, number][]; name: string }) => ({
      type: 'line',
      name: s.name,
      data: s.data.map(([dateStr, y]: [string, number]) => ({
        x: new Date(dateStr).getTime(),
        y,
        color: getInterpolatedColor(y),
        marker: { fillColor: getInterpolatedColor(y) },
      })),
    })),
    credits: {
      enabled: false,
    },
  }

  return <>{isLoading ? <ChartSkeleton fullWidth={true} /> : <BaseChart options={options} id={chartId} />}</>
}

export default GradientLineChart
